import { useQuery } from '@apollo/client'
import { Capacitor } from '@capacitor/core'
import gql from 'graphql-tag'
import { useEffect } from 'react'
import { isAndroid, isIOS } from 'react-device-detect'

import {
  ShortLinkQuery,
  ShortLinkQueryVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter()
  const shortLinkId = router.pathname.split('ulink/').pop()
  const { data } = useQuery<ShortLinkQuery, ShortLinkQueryVariables>(QUERY, {
    variables: { shortLinkId: shortLinkId ?? '' },
  })

  const isMobile = isAndroid || isIOS
  const isNative = Capacitor.isNativePlatform()
  const isWebOnly = !config.platform.includes('native')
  const isNativeOpenRequired = isMobile && !isNative && !isWebOnly

  useEffect(() => {
    // ネイティブで開く処理の必要がある場合はディープリンク起動・ストア遷移を行う
    if (!isNativeOpenRequired) return

    const url = window.location.href
    const strippedUrl = url.replace(/^https?:\/\//, '')

    // URLスキーマを使用してディープリンクURLを生成
    const appUrl = isAndroid
      ? `${config.appInfo.android.bundleId}://${strippedUrl}`
      : `${config.appInfo.ios.bundleId}://${strippedUrl}`

    // ディープリンクを開く処理
    if (isIOS) window.location.href = appUrl
    if (isAndroid) {
      const link = document.createElement('a')
      link.href = appUrl
      document.body.appendChild(link)
      link.click()
      link.remove()
    }

    // 一定時間後にストアへリダイレクト
    const now = Date.now()
    const timeout = 2000
    setTimeout(() => {
      if (Date.now() - now < timeout + 100) {
        window.location.href = isAndroid
          ? `https://play.google.com/store/apps/details?id=${config.appInfo.android.bundleId}`
          : `https://apps.apple.com/app/id${config.appInfo.ios.storeId}`
      }
    }, timeout)
  }, [])

  useEffect(() => {
    // ネイティブで開く処理の必要がない場合はリンク先に遷移
    if (!isNativeOpenRequired && data?.shortLink) {
      const redirectUrl = new URL(data.shortLink.redirectUrl)
      // deepLinkの場合stateにフラグを追加
      router.replace(redirectUrl.pathname, {
        fromDeeplink: Capacitor.isNativePlatform(),
      })
    }
  }, [data])

  return {
    state: {},
    actions: {},
  }
})

export const QUERY = gql`
  query shortLink($shortLinkId: ID!) {
    shortLink(id: $shortLinkId) {
      id
      redirectUrl
      shortLink
      createdAt
    }
  }
`

export const CREATE_SHORT_LINK_QUERY = gql`
  mutation CreateShortLink($redirectUrl: String!) {
    createShortLink(redirectUrl: $redirectUrl) {
      createdAt
      id
      redirectUrl
      shortLink
    }
  }
`
